/**
 * Что человек увидит в уведомлении и когда его вообще будить.
 *
 * Живёт в контрактах, а не в терминале, по той же причине, по которой в
 * общем пакете живёт вычислитель правил: уведомление приходит и на
 * экран, и в телефон, и решать, слать ли его, обязан один и тот же код.
 * Два набора условий — «на экране показалось, а в телефон не пришло» —
 * человеку объяснить нечем.
 *
 * Антиспам считается скользящим окном на источник. Десять уведомлений в
 * час — это не «десять и тишина навсегда», а десять за последний час:
 * бот, сорвавшийся в поток сигналов, не должен превращать телефон в
 * будильник, но и замолкать после этого насовсем не должен.
 *
 * Источник здесь — не «бот вообще», а конкретный: у сигнала из
 * конструктора это сам индикатор. Три индикатора считаются по
 * отдельности, и срабатывания одного не отнимают тишину у других.
 */
import { shouldNotify } from './settings.js';
const HOUR_MS = 3_600_000;
/**
 * Как назвать сигнал человеку.
 *
 * Порядок продуман под экран блокировки: там видна одна строка, и в
 * неё должно поместиться всё, ради чего человек станет разблокировать
 * телефон, — **что за монета, куда и на каком таймфрейме**. Имя
 * индикатора уходит во вторую строку: оно отвечает на вопрос «кто это
 * сказал», а он второй по важности.
 *
 * Служебных имён здесь нет. `rule-mtewjzr8` человек не узнаёт, а
 * «Следование тренду» узнаёт сразу.
 */
export function notificationText(signal) {
    const source = signalSourceName(signal);
    /*
     * Объяснение простыми словами — отдельной строкой, если источник его
     * оставил.
     *
     * «Дивергенции · сила 84 · цена 77000» — это адрес события, а не его
     * смысл: человека, которого разбудило уведомление, оно оставляет с
     * вопросом «и что». Источник, умеющий сказать по-человечески, обязан
     * это сказать здесь же — второго места у уведомления нет.
     */
    const plain = signal.meta?.rule?.plain ?? signal.meta?.comment;
    const body = `${source}${strength(signal)} · цена ${signal.price}`;
    return {
        title: `${signal.symbol} · ${sideWord(signal.side)} · ${signal.timeframe}`,
        body: plain ? `${body}
${plain}` : body,
    };
}
/**
 * Чей сигнал — именем, а не номером.
 *
 * У бота нет `meta.rule`, и раньше в уведомление попадал его `id`:
 * «bot-lq3x9a · цена 64000». Бот кладёт своё имя в `meta.botName`.
 */
export function signalSourceName(signal) {
    const botName = signal.meta?.['botName'];
    return signal.meta?.rule?.ruleName ?? (typeof botName === 'string' ? `Бот «${botName}»` : signal.botId);
}
/**
 * Сторона словом.
 *
 * `ALERT` — это событие без направления: условие выполнилось, а куда
 * идти, правило не говорит. Показывать «ALERT» человеку нельзя: он
 * прочтёт это как тревогу, а не как «сработало».
 */
function sideWord(side) {
    if (side === 'LONG')
        return 'LONG';
    if (side === 'SHORT')
        return 'SHORT';
    return 'сигнал';
}
/**
 * Сила — только когда она что-то значит.
 *
 * У события без направления её нет: там сто ставится не потому, что
 * сигнал сильный, а потому, что мерить нечем. Показать «сила 100» на
 * таком событии — соврать.
 */
function strength(signal) {
    if (signal.side !== 'LONG' && signal.side !== 'SHORT')
        return '';
    /*
     * Радар на своих условиях силы не меряет: монета либо прошла условия,
     * либо нет, и «сила 60» была заглушкой. У сделки бота «сила 100» — тоже.
     * Сила остаётся там, где её считают: у индикаторов и событий радара.
     */
    const radar = signal.meta?.['radar'];
    if (radar?.from === 'filters' || signal.sourceKind === 'bot')
        return '';
    return ` · сила ${signal.score}`;
}
/**
 * Решение «будить или нет» с памятью.
 *
 * Отдельно от `shouldNotify`, потому что тому не нужно состояние: он
 * отвечает на вопрос о сигнале и профиле. Здесь добавляется то, что
 * зависит от истории, — повторы и частота.
 */
export class NotificationGate {
    now;
    /** Источник → времена отправленных уведомлений за последний час. */
    #recent = new Map();
    /** Уже будили этим сигналом: ревизия не должна звонить снова. */
    #seen = new Set();
    constructor(now = Date.now) {
        this.now = now;
    }
    /**
     * Пропускать ли сигнал в уведомления.
     *
     * Порог отображения здесь не участвует: у уведомлений свой порог,
     * обычно выше. Видеть на графике и быть разбуженным — разные вещи.
     */
    allow(signal, settings) {
        const at = this.now();
        if (!shouldNotify(signal, settings, at))
            return false;
        // Сигнал, о котором уже звонили. Бот вправе прислать его повторно с
        // новым статусом — будить второй раз незачем.
        if (this.#seen.has(signal.id))
            return false;
        /*
         * Предел берётся по роду источника.
         *
         * У ботов и у индикаторов он свой: бот может сорваться в поток
         * сигналов чужим кодом, а индикатор человек настроил сам и знает,
         * как часто тот срабатывает. Один потолок на двоих заставлял бы
         * выбирать между спамом от одного и молчанием от другого.
         */
        const limits = settings.notifications.limits[signal.sourceKind === 'rule' ? 'rule' : 'bot'];
        // Ограничение выключено — считать нечего и помнить нечего.
        if (!limits.rateEnabled) {
            this.#seen.add(signal.id);
            return true;
        }
        const limit = limits.maxPerHour;
        if (limit <= 0)
            return false;
        const history = (this.#recent.get(signal.botId) ?? []).filter((time) => at - time < HOUR_MS);
        if (history.length >= limit) {
            // Молча глотаем: источник, сорвавшийся в поток сигналов, не должен
            // превращать уведомления в мигающую ёлку.
            this.#recent.set(signal.botId, history);
            return false;
        }
        history.push(at);
        this.#recent.set(signal.botId, history);
        this.#seen.add(signal.id);
        return true;
    }
    reset() {
        this.#recent.clear();
        this.#seen.clear();
    }
}
//# sourceMappingURL=notify.js.map